// src/pages/MasterClassPage.js
import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import axios from "axios";
import {
  Calendar,
  Clock,
  Users,
  Star,
  X,
  ChevronRight,
  Play,
  BookOpen,
  Video,
  Loader,
  AlertCircle,
  CheckCircle,
  Target,
  Coffee,
  Award,
} from "lucide-react";
import { NeuralNoise } from "../components/ui/neural-noise";
import { useDynamicCSS } from "../hooks/DynamicCSSLoader";

/* =========================
   Error Boundary
========================= */
class ErrorBoundary extends React.Component {
  state = { hasError: false };
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error, errorInfo) {
    // eslint-disable-next-line no-console
    console.error("ErrorBoundary caught:", error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="mcPage">
          <div className="mcContainer">
            <div className="mcStateBox mcErrorBox">
              <AlertCircle className="mcStateIcon" />
              <p>Something went wrong. Please refresh and try again.</p>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const MasterClassPage = () => {
  useDynamicCSS("masterclass");

  const [isRegistrationOpen, setIsRegistrationOpen] = useState(false);
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const [animatedMasterclasses, setAnimatedMasterclasses] = useState([]);
  const [currentMasterclass, setCurrentMasterclass] = useState(null);

  const [upcomingMasterclasses, setUpcomingMasterclasses] = useState([]);
  const [pastRecordings, setPastRecordings] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [isLive, setIsLive] = useState(false);
  const [selectedFormat, setSelectedFormat] = useState("all");
  const [expandedFaq, setExpandedFaq] = useState(null);

  const [emailSubscribe, setEmailSubscribe] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    experience: "beginner",
  });

  const [featuredMasterclass, setFeaturedMasterclass] = useState(null);

  const hasScrollListenerRef = useRef(false);
  const animatedSetRef = useRef(new Set());

  // ✅ Theme sync
  const [theme, setTheme] = useState(
    document?.documentElement?.getAttribute("data-theme") || "dark"
  );

  useEffect(() => {
    const el = document.documentElement;
    const update = () => setTheme(el.getAttribute("data-theme") || "dark");
    update();
    const obs = new MutationObserver(update);
    obs.observe(el, { attributes: true, attributeFilter: ["data-theme"] });
    return () => obs.disconnect();
  }, []);

  const noiseColor = useMemo(() => {
    return theme === "light" ? [0.2, 0.45, 1.0] : [0.9, 0.55, 0.15];
  }, [theme]);

  const noiseOpacity = theme === "light" ? 0.18 : 0.45;

  /* =========================
     ✅ ONLY PLACE for your 2 recording videos
     (Update links here only)
  ========================= */
  const manualPastRecordings = useMemo(
    () => [
      {
        id: "yt-1",
        title: "AI Masterclass – Foundations & Career Roadmap",
        date: "2024-12-10",
        time: "Recorded Session",
        instructor: "CraftingBrain Mentor",
        image: "https://img.youtube.com/vi/W4QId24TiMI/hqdefault.jpg",
        videoUrl: "https://www.youtube.com/embed/W4QId24TiMI",
        format: "online",
        duration: "2 Hours",
        place: "Online",
        topics: [
          "AI fundamentals explained",
          "Career roadmap in AI",
          "Real-world applications",
        ],
        participants: 1200,
        rating: 5,
      },
      {
        id: "yt-2",
        title: "Generative AI & ChatGPT – Hands-on Workshop",
        date: "2024-11-28",
        time: "Recorded Session",
        instructor: "CraftingBrain Expert",
        image: "https://img.youtube.com/vi/voCmcY6L9zo/hqdefault.jpg",
        videoUrl: "https://www.youtube.com/embed/voCmcY6L9zo",
        format: "online",
        duration: "2.5 Hours",
        place: "Online",
        topics: ["How ChatGPT works", "Prompt engineering", "Live demos & use-cases"],
        participants: 1800,
        rating: 5,
      },
    ],
    []
  );

  /* =========================
     Testimonials + FAQs
  ========================= */
  const testimonials = [
    {
      id: 1,
      name: "Priya Sharma",
      college: "Sphoorthy Engineering College",
      text: "The masterclass gave me clarity on my AI career path. The hands-on session was extremely valuable! I built my first ML project right after attending.",
      rating: 5,
    },
    {
      id: 2,
      name: "Rahul Kumar",
      college: "IARE College",
      text: "I built my first ML model during the session. The instructors were very supportive and patient with all our questions. Highly recommended!",
      rating: 5,
    },
    {
      id: 3,
      name: "Sneha Reddy",
      profession: "Software Developer",
      text: "Best free learning resource I've found. The content is industry-relevant and practical. This helped me transition into a Data Science role!",
      rating: 5,
    },
  ];

  const faqs = [
    {
      question: "Are the masterclasses really free?",
      answer:
        "Yes! All our masterclasses are completely free with no hidden charges. We believe in making quality AI education accessible to everyone.",
    },
    {
      question: "Will I get a certificate?",
      answer:
        "Yes, you'll receive a participation certificate after completing the masterclass. The certificate includes your name, masterclass topic, date, and can be added to your LinkedIn profile.",
    },
    {
      question: "Do I need any prior experience?",
      answer:
        "It depends on the masterclass. Each session has specific prerequisites listed in its description. We offer both beginner-friendly and advanced sessions.",
    },
    {
      question: "Are recordings available?",
      answer:
        "Yes! Registered attendees get access to high-quality recordings for 7 days after the live session. However, we highly recommend attending live for real-time interaction.",
    },
    {
      question: "Can I attend multiple masterclasses?",
      answer:
        "Absolutely! You can register for as many masterclasses as you'd like. We encourage continuous learning across different AI domains.",
    },
    {
      question: "What if I can't attend live?",
      answer:
        "No worries! We'll send you the recording link via email. However, live sessions allow you to ask questions, participate in hands-on coding, and network with peers.",
    },
    {
      question: "What equipment do I need?",
      answer:
        "For online sessions: A laptop/desktop with stable internet connection. For hands-on sessions, you'll need Python installed. For offline sessions: Bring your laptop for practical exercises.",
    },
    {
      question: "How do I join offline masterclasses?",
      answer:
        "Register with your college ID, check the venue details in confirmation email, and arrive 15 minutes early. Bring your laptop for hands-on practice sessions.",
    },
  ];

  /* =========================
     Helpers
  ========================= */
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return "Date TBA";
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const normalizeMasterclass = (masterclass, index) => {
    const title = masterclass.Name || masterclass.title || "Untitled Masterclass";
    const date =
      masterclass.Date ||
      masterclass.date ||
      new Date().toISOString().split("T")[0];

    return {
      ...masterclass,
      id:
        masterclass.id ||
        masterclass._id ||
        `masterclass-${index}-${String(title).replace(/\s+/g, "-")}-${String(date)}`,
      title,
      date,
      instructor: masterclass.author || masterclass.instructor || "CraftingBrain Expert",
      time: masterclass.time || "TBD",
      image:
        masterclass.image ||
        "https://images.unsplash.com/photo-1517180102446-f3ece451e9d8?w=900&h=600&fit=crop",
      participants: Number(masterclass.participants || 0),
      rating: Number(masterclass.rating || 0),
      instructorTitle: masterclass.instructorTitle || "AI/ML Instructor",
      instructorImage: masterclass.instructorImage || "/amansir1.png",
      isLive: Boolean(masterclass.isLive || false),
      liveVideoUrl: masterclass.liveVideoUrl || masterclass.registerLink || "",
      videoUrl:
        masterclass.videoUrl ||
        masterclass.recordingUrl ||
        masterclass.liveVideoUrl ||
        masterclass.registerLink ||
        "https://www.youtube.com/embed/dQw4w9WgXcQ",
      description:
        masterclass.description ||
        "Join our comprehensive masterclass on cutting-edge AI and ML topics.",
      place: masterclass.place || "Online",
      format:
        masterclass.format ||
        (String(masterclass.place || "").toLowerCase() === "online" ? "online" : "offline"),
      duration: masterclass.duration || "2-3 hours",
      topics: Array.isArray(masterclass.topics) ? masterclass.topics : [],
      audience: masterclass.audience || "Students and professionals",
      prerequisites: masterclass.prerequisites || "None",
    };
  };

  /* =========================
     Categorize
  ========================= */
  const categorizeMasterclasses = useCallback((masterclassesData) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    const upcoming = [];
    const past = [];

    masterclassesData.forEach((mc) => {
      const raw = mc.date || mc.Date;
      const parsed = raw ? new Date(raw) : null;

      if (parsed && !Number.isNaN(parsed.getTime())) {
        if (parsed >= today) upcoming.push(mc);
        else past.push(mc);
      } else {
        past.push(mc);
      }
    });

    upcoming.sort((a, b) => new Date(a.date) - new Date(b.date));
    past.sort((a, b) => new Date(b.date) - new Date(a.date));

    setUpcomingMasterclasses(upcoming);
    setPastRecordings(past);
    setFeaturedMasterclass(upcoming[0] || past[0] || null);
  }, []);

  /* =========================
     Mock fallback
  ========================= */
  const loadMockData = useCallback(() => {
    const mockMasterclasses = []; // ✅ no blank object

    const normalized = mockMasterclasses.map((m, i) => normalizeMasterclass(m, i));

    categorizeMasterclasses(normalized);

    // ✅ in fallback also, show ONLY your 2 recordings
    setPastRecordings([...manualPastRecordings].sort((a, b) => new Date(b.date) - new Date(a.date)));
    setFeaturedMasterclass((prev) => prev || manualPastRecordings[0] || null);
  }, [categorizeMasterclasses, manualPastRecordings]);

  /* =========================
     Fetch from API
  ========================= */
  const fetchMasterclasses = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(
        "https://8zrj1t4nx2.execute-api.us-east-1.amazonaws.com/wk/getWk",
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        }
      );

      if (!response.ok) throw new Error(`HTTP error ${response.status}`);

      const data = await response.json();
      const list = (data.workshops || data || []).map((m, i) =>
        normalizeMasterclass(m, i)
      );

      categorizeMasterclasses(list);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Error fetching masterclasses:", err);
      setError("Failed to load masterclasses. Showing fallback content.");
      loadMockData();
    } finally {
      setLoading(false);
    }
  }, [categorizeMasterclasses, loadMockData]);

  /* =========================
     Mount
     ✅ Remove old existing videos + inject only these 2
  ========================= */
  useEffect(() => {
    fetchMasterclasses().then(() => {
      setPastRecordings((prev) => {
        const manualIds = new Set(manualPastRecordings.map((x) => x.id));
        const manualUrls = new Set(manualPastRecordings.map((x) => x.videoUrl));

        // remove any old copies of those 2 (by id OR by videoUrl)
        const cleaned = (prev || []).filter(
          (x) => !manualIds.has(x.id) && !manualUrls.has(x.videoUrl)
        );

        const merged = [...manualPastRecordings, ...cleaned];

        // final dedupe safety
        const seen = new Set();
        const unique = [];
        for (const item of merged) {
          const key = `${item.id}__${item.videoUrl}`;
          if (!seen.has(key)) {
            seen.add(key);
            unique.push(item);
          }
        }

        unique.sort((a, b) => new Date(b.date) - new Date(a.date));
        return unique;
      });
    });
  }, [fetchMasterclasses, manualPastRecordings]);

  /* =========================
     Live status (safe)
  ========================= */
  useEffect(() => {
    if (!featuredMasterclass) return;

    const checkLiveStatus = () => {
      const now = new Date();
      const dt = new Date(featuredMasterclass.date);
      if (Number.isNaN(dt.getTime())) {
        setIsLive(false);
        return;
      }
      const hoursDiff = Math.abs(now - dt) / (1000 * 60 * 60);
      setIsLive(hoursDiff < 2 && Boolean(featuredMasterclass.isLive));
    };

    checkLiveStatus();
    const interval = setInterval(checkLiveStatus, 60000);
    return () => clearInterval(interval);
  }, [featuredMasterclass]);

  /* =========================
     Scroll reveal
  ========================= */
  useEffect(() => {
    if (hasScrollListenerRef.current) return;
    hasScrollListenerRef.current = true;

    const revealVisibleCards = () => {
      const sections = document.querySelectorAll(".mcSection");
      sections.forEach((section) => {
        const rect = section.getBoundingClientRect();
        const visible = rect.top < window.innerHeight && rect.bottom > 0;
        if (!visible) return;

        const cards = section.querySelectorAll(".mcCard");
        cards.forEach((card, idx) => {
          const id = card.getAttribute("data-id");
          if (!id) return;

          if (!animatedSetRef.current.has(id)) {
            animatedSetRef.current.add(id);
            setTimeout(() => {
              setAnimatedMasterclasses((prev) =>
                prev.includes(id) ? prev : [...prev, id]
              );
            }, idx * 120);
          }
        });
      });
    };

    window.addEventListener("scroll", revealVisibleCards, { passive: true });
    requestAnimationFrame(() => revealVisibleCards());

    return () => window.removeEventListener("scroll", revealVisibleCards);
  }, []);

  useEffect(() => {
    requestAnimationFrame(() => {
      window.dispatchEvent(new Event("scroll"));
    });
  }, [upcomingMasterclasses, pastRecordings]);

  /* =========================
     Form validation + submit
  ========================= */
  const validateForm = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^[\d\s\-+()]{10,}$/;

    if (!formData.name.trim()) return "Name is required";
    if (!formData.email.trim()) return "Email is required";
    if (!emailRegex.test(formData.email)) return "Please enter a valid email address";
    if (!formData.phone.trim()) return "Phone number is required";
    if (!phoneRegex.test(formData.phone)) return "Please enter a valid phone number";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const msg = validateForm();
    if (msg) {
      setError(msg);
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const payload = {
        ...formData,
        workshop: (currentMasterclass || featuredMasterclass)?.title || "",
      };

      await axios.post(
        "https://h5kp0hj673.execute-api.us-east-1.amazonaws.com/wkr/wkr",
        payload,
        { timeout: 10000 }
      );

      setIsRegistrationOpen(false);
      setFormData({ name: "", email: "", phone: "", experience: "beginner" });
      alert("Registration successful! Check your email for details.");
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Submission error:", err);
      setError("Submission failed. Please check your connection and try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEmailSubscribe = (e) => {
    e.preventDefault();
    if (!emailSubscribe) return;
    alert(`Thanks for subscribing! We'll notify you at ${emailSubscribe}`);
    setEmailSubscribe("");
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        setIsRegistrationOpen(false);
        setIsVideoOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  /* =========================
     Loading/Error UI
  ========================= */
  if (loading) {
    return (
      <div className="mcPage">
        <NeuralNoise color={noiseColor} opacity={noiseOpacity} speed={0.00025} />
        <div className="mcContainer">
          <div className="mcStateBox">
            <Loader className="mcSpin" />
            <p>Loading masterclasses...</p>
          </div>
        </div>
      </div>
    );
  }


  const filteredMasterclasses =
    selectedFormat === "all"
      ? upcomingMasterclasses
      : upcomingMasterclasses.filter((mc) => mc.format === selectedFormat);

  /* =========================
     Card component
  ========================= */
  const MasterClassCard = ({ masterclass, index, isUpcoming = false }) => {
    const masterclassId = String(masterclass.id || `masterclass-${index}`);

    const isAnimated =
      animatedMasterclasses.length === 0 || animatedMasterclasses.includes(masterclassId);

    return (
      <div
        className={`mcCard ${isAnimated ? "mcIn" : ""} ${isUpcoming ? "mcUpcoming" : "mcPast"}`}
        style={{ animationDelay: `${index * 0.08}s` }}
        data-id={masterclassId}
        onClick={() => {
          setCurrentMasterclass(masterclass);
          if (isUpcoming) setIsRegistrationOpen(true);
          else setIsVideoOpen(true);
        }}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            setCurrentMasterclass(masterclass);
            if (isUpcoming) setIsRegistrationOpen(true);
            else setIsVideoOpen(true);
          }
        }}
      >
        <div className="mcCardTop">
          <span className={`mcPill ${masterclass.format}`}>
            {masterclass.format === "online" ? "💻 Online" : "🏢 Offline"}
          </span>
          <span className={`mcTag ${isUpcoming ? "up" : "rec"}`}>
            {isUpcoming ? "Upcoming" : "Recording"}
          </span>
        </div>

        <div className="mcThumb">
          <img src={masterclass.image} alt={masterclass.title} />
          <div className="mcThumbOverlay">
            <div className="mcPlayBtn">
              <Play size={18} />
            </div>
          </div>
        </div>

        <div className="mcBody">
          <h3 className="mcTitle">{masterclass.title}</h3>

          <div className="mcMeta">
            <div className="mcMetaItem">
              <Calendar size={16} />
              <span>{formatDate(masterclass.date)}</span>
            </div>
            <div className="mcMetaItem">
              <Clock size={16} />
              <span>{masterclass.time}</span>
            </div>

            {masterclass.duration && (
              <div className="mcMetaItem">
                <Clock size={16} />
                <span>{masterclass.duration}</span>
              </div>
            )}

            {masterclass.place && masterclass.format === "offline" && (
              <div className="mcMetaItem">
                <span className="mcPin">📍</span>
                <span>{masterclass.place}</span>
              </div>
            )}
          </div>

          {masterclass.topics?.length > 0 && (
            <div className="mcTopics">
              <div className="mcTopicsTitle">What you’ll learn</div>
              <ul>
                {masterclass.topics.slice(0, 3).map((topic, idx) => (
                  <li key={idx}>{topic}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="mcStatsRow">
            <div className="mcStat">
              <Users size={16} />
              <span>{masterclass.participants || 0} registered</span>
            </div>
            {masterclass.rating > 0 && (
              <div className="mcStat">
                <Star size={16} />
                <span>{masterclass.rating}/5</span>
              </div>
            )}
          </div>

          <div className="mcInstructor">by {masterclass.instructor}</div>

          <div className="mcCardCta">
            {isUpcoming ? (
              <button
                className="mcBtn mcBtnPrimary mcBtnFull"
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentMasterclass(masterclass);
                  setIsRegistrationOpen(true);
                }}
              >
                Register Now — It’s Free <ChevronRight size={18} />
              </button>
            ) : (
              <button
                className="mcBtn mcBtnGhost mcBtnFull"
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentMasterclass(masterclass);
                  setIsVideoOpen(true);
                }}
              >
                <Video size={18} />
                Watch Recording
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  /* =========================
     Render
  ========================= */
  return (
    <ErrorBoundary>
      <div className="mcPage">
        <NeuralNoise color={noiseColor} opacity={noiseOpacity} speed={0.00025} />

        <div className="mcShell">
          {/* HERO */}
          <section className="mcHero">
            <div className="mcContainer">
              <div className="mcHeroGrid">
                <div className="mcHeroLeft">
                  <div className="mcBadge">
                    <span className="mcBadgeFree">FREE</span>
                    <span className="mcBadgeText">MASTERCLASS</span>
                  </div>

                  <h1 className="mcHeroH1">
                    <span className="mcHeroMain">Upgrade Your Skills in</span>
                    <span className="mcHeroSub">AI • Data Science • GenAI</span>
                  </h1>

                  <p className="mcHeroP">
                    Attend premium hands-on sessions led by industry professionals. Choose{" "}
                    <strong>Online</strong> or <strong>Offline</strong>—learn, build projects,
                    and get certified.
                  </p>

                  <div className="mcHeroStats">
                    <div className="mcHeroStat">
                      <div className="mcHeroNum">5000+</div>
                      <div className="mcHeroLbl">Students Trained</div>
                    </div>
                    <div className="mcHeroStat">
                      <div className="mcHeroNum">50+</div>
                      <div className="mcHeroLbl">Masterclasses</div>
                    </div>
                    <div className="mcHeroStat">
                      <div className="mcHeroNum">4.8/5</div>
                      <div className="mcHeroLbl">Avg Rating</div>
                    </div>
                    <div className="mcHeroStat">
                      <div className="mcHeroNum">100%</div>
                      <div className="mcHeroLbl">Free</div>
                    </div>
                  </div>

                  <div className="mcHeroBtns">
                    <button
                      className="mcBtn mcBtnPrimary"
                      onClick={() => {
                        document
                          .querySelector(".upcoming-masterclasses")
                          ?.scrollIntoView({ behavior: "smooth" });
                      }}
                    >
                      Browse Masterclasses <ChevronRight size={18} />
                    </button>

                    <button
                      className="mcBtn mcBtnGhost"
                      onClick={() => {
                        document
                          .querySelector(".recordings-section")
                          ?.scrollIntoView({ behavior: "smooth" });
                      }}
                    >
                      <Video size={18} />
                      View Past Recordings
                    </button>
                  </div>

                  {featuredMasterclass && (
                    <div className="mcFeaturedStrip">
                      <div className="mcFeaturedIcon">
                        <CheckCircle size={18} />
                      </div>
                      <div className="mcFeaturedText">
                        <div className="mcFeaturedTop">
                          Featured: <strong>{featuredMasterclass.title}</strong>
                          {isLive ? <span className="mcLiveDot">LIVE</span> : null}
                        </div>
                        <div className="mcFeaturedSub">
                          {formatDate(featuredMasterclass.date)} • {featuredMasterclass.time}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="mcHeroRight">
                  <div className="mcHeroCard">
                    <div className="mcInstructorWrap">
                      <img
                        src="amansir.png"
                        alt="Instructor"
                        className="mcInstructorImg"
                      />
                    </div>

                    <div className="mcHeroCardBody">
                      <div className="mcHeroCardKicker">Mentor-led sessions</div>
                      <div className="mcHeroCardTitle">Learn by building</div>
                      <div className="mcHeroCardDesc">
                        Real projects, templates, and guided practice — exactly like your Home page
                        premium style.
                      </div>

                      <div className="mcMiniRow">
                        <div className="mcMini">
                          <Target size={18} />
                          <span>Industry Focus</span>
                        </div>
                        <div className="mcMini">
                          <Coffee size={18} />
                          <span>Hands-on</span>
                        </div>
                        <div className="mcMini">
                          <Award size={18} />
                          <span>Certificate</span>
                        </div>
                      </div>

                      <button
                        className="mcBtn mcBtnPrimary mcBtnFull"
                        onClick={() => {
                          document
                            .querySelector(".upcoming-masterclasses")
                            ?.scrollIntoView({ behavior: "smooth" });
                        }}
                      >
                        Join Next Session <ChevronRight size={18} />
                      </button>
                    </div>

                    <div className="mcHeroGlow a" />
                    <div className="mcHeroGlow b" />
                    <div className="mcHeroGlow c" />
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* WHY ATTEND */}
          <section className="mcWhy">
            <div className="mcContainer">
              <h2 className="mcH2">Why Attend Our Masterclasses?</h2>
              <p className="mcSub">What makes CraftingBrain sessions different</p>

              <div className="mcGrid6">
                <div className="mcInfoCard">
                  <div className="mcInfoIcon"><Target size={26} /></div>
                  <h3>Industry-Focused Content</h3>
                  <p>Learn what companies actually need — built from real job requirements.</p>
                </div>

                <div className="mcInfoCard">
                  <div className="mcInfoIcon"><Coffee size={26} /></div>
                  <h3>Hands-On Practice</h3>
                  <p>Code along live and build portfolio-ready projects during the session.</p>
                </div>

                <div className="mcInfoCard">
                  <div className="mcInfoIcon"><Users size={26} /></div>
                  <h3>Expert Instructors</h3>
                  <p>Learn from professionals with real industry experience in AI/ML.</p>
                </div>

                <div className="mcInfoCard">
                  <div className="mcInfoIcon"><Video size={26} /></div>
                  <h3>Flexible Formats</h3>
                  <p>Online or Offline — choose what fits your schedule and learning style.</p>
                </div>

                <div className="mcInfoCard">
                  <div className="mcInfoIcon"><BookOpen size={26} /></div>
                  <h3>Resources & Templates</h3>
                  <p>Get code templates, datasets, and projects worth thousands — free.</p>
                </div>

                <div className="mcInfoCard">
                  <div className="mcInfoIcon"><Award size={26} /></div>
                  <h3>Completion Certificate</h3>
                  <p>Get a certificate and add it to your resume + LinkedIn profile.</p>
                </div>
              </div>
            </div>
          </section>

          {/* UPCOMING */}
          <section className="mcSection upcoming-masterclasses">
            <div className="mcContainer">
              <h2 className="mcH2">Upcoming Masterclasses</h2>
              <p className="mcSub">Register now to secure your spot</p>

              <div className="mcFilters">
                <button
                  className={`mcFilter ${selectedFormat === "all" ? "on" : ""}`}
                  onClick={() => setSelectedFormat("all")}
                >
                  All
                </button>
                <button
                  className={`mcFilter ${selectedFormat === "online" ? "on" : ""}`}
                  onClick={() => setSelectedFormat("online")}
                >
                  💻 Online
                </button>
                <button
                  className={`mcFilter ${selectedFormat === "offline" ? "on" : ""}`}
                  onClick={() => setSelectedFormat("offline")}
                >
                  🏢 Offline
                </button>
              </div>

              {filteredMasterclasses.length === 0 ? (
                <div className="mcEmpty">No upcoming masterclasses for this filter yet.</div>
              ) : (
                <div className="mcGrid">
                  {filteredMasterclasses.map((mc, index) => (
                    <MasterClassCard key={mc.id} masterclass={mc} index={index} isUpcoming />
                  ))}
                </div>
              )}
            </div>
          </section>

          {/* PAST RECORDINGS */}
          <section className="mcSection recordings-section">
            <div className="mcContainer">
              <h2 className="mcH2">Past Masterclass Recordings</h2>
              <p className="mcSub">Learn anytime with previously conducted sessions</p>

              {pastRecordings.length === 0 ? (
                <div className="mcEmpty">🎥 Recordings will appear here after the first live session.</div>
              ) : (
                <div className="mcGrid">
                  {pastRecordings.slice(0, 6).map((mc, index) => (
                    <MasterClassCard
                      key={`${mc.id}-${mc.videoUrl}`}
                      masterclass={mc}
                      index={index}
                      isUpcoming={false}
                    />
                  ))}
                </div>
              )}
            </div>
          </section>

          {/* HOW TO JOIN */}
          <section className="mcHow">
            <div className="mcContainer">
              <h2 className="mcH2">How to Join</h2>

              <div className="mcJoinGrid">
                <div className="mcJoinCard">
                  <h3>💻 For Online Masterclasses</h3>
                  <ol>
                    <li>Register using the form on the masterclass card</li>
                    <li>You’ll receive a confirmation email with the meeting link</li>
                    <li>Join 10 minutes early to test your setup</li>
                    <li>Ask questions, code along, and network with peers</li>
                  </ol>
                </div>

                <div className="mcJoinCard">
                  <h3>🏢 For Offline Masterclasses</h3>
                  <ol>
                    <li>Check venue/location in confirmation email</li>
                    <li>Register with your college/professional details</li>
                    <li>Arrive 15 minutes early</li>
                    <li>Bring your laptop for hands-on practice</li>
                  </ol>
                </div>
              </div>
            </div>
          </section>

          {/* TESTIMONIALS */}
          <section className="mcTestimonials">
            <div className="mcContainer">
              <h2 className="mcH2">What Our Attendees Say</h2>
              <p className="mcSub">Real feedback from students and professionals</p>

              <div className="mcGrid3">
                {testimonials.map((t) => (
                  <div key={t.id} className="mcTCard">
                    <div className="mcTRating">{"⭐".repeat(t.rating)}</div>
                    <p className="mcTText">“{t.text}”</p>
                    <div className="mcTAuthor">
                      <div className="mcTName">{t.name}</div>
                      <div className="mcTMeta">{t.college || t.profession}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="mcFaq">
            <div className="mcContainer">
              <h2 className="mcH2">Frequently Asked Questions</h2>
              <p className="mcSub">Got questions? We’ve got answers</p>

              <div className="mcFaqList">
                {faqs.map((faq, index) => (
                  <div
                    key={index}
                    className={`mcFaqItem ${expandedFaq === index ? "open" : ""}`}
                    onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter")
                        setExpandedFaq(expandedFaq === index ? null : index);
                    }}
                  >
                    <div className="mcFaqQ">
                      <h3>{faq.question}</h3>
                      <span className="mcFaqI">{expandedFaq === index ? "−" : "+"}</span>
                    </div>
                    <div className="mcFaqA">
                      <p>{faq.answer}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* PARTNERS */}
          <section className="mcPartners">
            <div className="mcContainer">
              <h2 className="mcH2">Our Partner Colleges</h2>
              <p className="mcSub">
                Our masterclasses are conducted in partnership with leading institutions
              </p>

              <div className="mcPartnersGrid">
                <div className="mcPartner">Sphoorthy Engineering College</div>
                <div className="mcPartner">IARE College</div>
                <div className="mcPartner">CMR Engineering College</div>
                <div className="mcPartner">VNR VJIET</div>
              </div>

              <div className="mcPartnerCTA">
                <p>Want to partner with us?</p>
                <button className="mcBtn mcBtnPrimary">Contact Us for College Partnership</button>
              </div>
            </div>
          </section>

          {/* FINAL CTA */}
          <section className="mcFinal">
            <div className="mcContainer">
              <div className="mcFinalBox">
                <h2>Ready to Start Your AI Journey?</h2>
                <p>
                  Don’t miss our next masterclass! Register now and take the first step toward your AI career.
                </p>

                <div className="mcFinalBtns">
                  <button
                    className="mcBtn mcBtnPrimary mcBtnLarge"
                    onClick={() =>
                      document
                        .querySelector(".upcoming-masterclasses")
                        ?.scrollIntoView({ behavior: "smooth" })
                    }
                  >
                    Register for Next Masterclass
                  </button>

                  <button
                    className="mcBtn mcBtnGhost mcBtnLarge"
                    onClick={() =>
                      document
                        .querySelector(".upcoming-masterclasses")
                        ?.scrollIntoView({ behavior: "smooth" })
                    }
                  >
                    Browse All Masterclasses
                  </button>
                </div>

                <div className="mcEmail">
                  <p>📧 Get notified about upcoming masterclasses</p>
                  <form className="mcEmailForm" onSubmit={handleEmailSubscribe}>
                    <input
                      type="email"
                      placeholder="Enter your email address"
                      value={emailSubscribe}
                      onChange={(e) => setEmailSubscribe(e.target.value)}
                      required
                    />
                    <button type="submit" className="mcBtn mcBtnPrimary">
                      Subscribe
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </section>

          {/* Registration Modal */}
          {isRegistrationOpen && (
            <div className="mcModalOverlay" onClick={() => setIsRegistrationOpen(false)}>
              <div className="mcModal" onClick={(e) => e.stopPropagation()}>
                <div className="mcModalHead">
                  <h2>Register for Masterclass</h2>
                  <button className="mcIconBtn" onClick={() => setIsRegistrationOpen(false)}>
                    <X />
                  </button>
                </div>

                <div className="mcModalSub">
                  <p className="mcModalTitle">
                    <strong>{(currentMasterclass || featuredMasterclass)?.title}</strong>
                  </p>
                  <p className="mcModalDate">
                    {formatDate((currentMasterclass || featuredMasterclass)?.date)}
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="mcForm">
                  <div className="mcField">
                    <label htmlFor="name">Full Name *</label>
                    <input
                      type="text"
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      placeholder="Enter your full name"
                    />
                  </div>

                  <div className="mcField">
                    <label htmlFor="email">Email Address *</label>
                    <input
                      type="email"
                      id="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      placeholder="your.email@example.com"
                    />
                  </div>

                  <div className="mcField">
                    <label htmlFor="phone">Phone Number *</label>
                    <input
                      type="tel"
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      required
                      placeholder="+91 XXXXX XXXXX"
                    />
                  </div>

                  <div className="mcField">
                    <label htmlFor="experience">Experience Level</label>
                    <select
                      id="experience"
                      value={formData.experience}
                      onChange={(e) => setFormData({ ...formData, experience: e.target.value })}
                    >
                      <option value="beginner">Beginner</option>
                      <option value="intermediate">Intermediate</option>
                      <option value="advanced">Advanced</option>
                    </select>
                  </div>

                  {error && <div className="mcFormError">{error}</div>}

                  <button type="submit" className="mcBtn mcBtnPrimary mcBtnFull" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <Loader className="mcBtnSpin" size={16} />
                        Registering...
                      </>
                    ) : (
                      <>
                        <CheckCircle size={16} />
                        Register Now — It’s Free!
                      </>
                    )}
                  </button>

                  <p className="mcFormNote">* You’ll receive confirmation email with joining details</p>
                </form>
              </div>
            </div>
          )}

          {/* Video Modal */}
          {isVideoOpen && currentMasterclass && (
            <div className="mcModalOverlay" onClick={() => setIsVideoOpen(false)}>
              <div className="mcVideoModal" onClick={(e) => e.stopPropagation()}>
                <button className="mcIconBtn mcVideoClose" onClick={() => setIsVideoOpen(false)}>
                  <X />
                </button>
                <iframe
                  src={
                    isLive && currentMasterclass.liveVideoUrl
                      ? currentMasterclass.liveVideoUrl
                      : currentMasterclass.videoUrl
                  }
                  title="Masterclass Video"
                  frameBorder="0"
                  allowFullScreen
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default MasterClassPage;
